import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLang } from '../i18n/context';
import SEO from '../components/SEO';
import NotFound from './NotFound';
import { Container, Reveal } from '../components/Reveal';
import ArticleCard from '../components/ArticleCard';
import FAQ from '../components/FAQ';
import { WhatsAppButton } from '../components/CTAs';
import { articleSchema, faqSchema, breadcrumbSchema } from '../lib/schema';
import { fetchArticle, fetchArticles, readingMinutes } from '../lib/api';
import { track } from '../lib/track';
import { getTreatment } from '../data/treatments';
import { ShareRow } from '../../components/common/ShareRow';
import { DEFAULT_KOLUM_COVER } from '../lib/site';
import { onImgError } from '../../lib/images';

function renderInline(text, keyBase, langPrefix) {
    const parts = [];
    const re = /\[([^\]]+)\]\(([^)]+)\)/g;
    let last = 0;
    let k = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
        if (m.index > last) parts.push(text.slice(last, m.index));
        const href = m[2];
        const to = href.startsWith('/') ? `${langPrefix}${href}` : href;
        parts.push(
            <Link key={`${keyBase}-l${k++}`} to={to} className="font-semibold text-gold-700 underline underline-offset-4 transition-colors hover:text-ink">
                {m[1]}
            </Link>
        );
        last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
}

function renderTable(rows, key) {
    const [head, ...body] = rows;
    return (
        <div key={key} className="my-8 overflow-x-auto rounded-2xl border border-peri-200" data-testid="article-table">
            <table className="w-full min-w-[560px] border-collapse text-left text-[15px]">
                <thead>
                    <tr className="bg-peri-50">
                        {head.map((c, i) => (
                            <th key={i} className="border-b border-peri-200 px-4 py-3 font-semibold text-ink">{c}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {body.map((r, ri) => (
                        <tr key={ri} className="even:bg-peri-50/40">
                            {r.map((c, ci) => (
                                <td key={ci} className="border-b border-peri-100 px-4 py-3 align-top leading-relaxed text-ink-body">{c}</td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function renderBody(body, langPrefix = '') {
    const out = [];
    let table = [];
    (body || '').split('\n').forEach((line, i) => {
        const text = line.trim();
        if (text.startsWith('|')) {
            const cells = text.split('|').slice(1, -1).map((c) => c.trim());
            if (cells.some((c) => /^-{2,}$/.test(c))) return;
            if (cells.length >= 2) table.push(cells);
            return;
        }
        if (table.length > 0) {
            out.push(renderTable(table, `table-${i}`));
            table = [];
        }
        if (!text) return;
        const imgMatch = text.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
        if (imgMatch) {
            return out.push(
                <figure key={i} className="my-8">
                    <img src={imgMatch[2]} alt={imgMatch[1]} loading="lazy" onError={onImgError('dental')} className="w-full rounded-3xl object-cover" data-testid="article-body-img" />
                    {imgMatch[1] && <figcaption className="mt-2 text-center text-xs italic leading-relaxed text-ink-subtle">{imgMatch[1]}</figcaption>}
                </figure>
            );
        }
        if (text.startsWith('## ')) {
            return out.push(
                <h2 key={i} className="mt-10 font-serif text-2xl font-semibold text-ink md:text-3xl">
                    {text.slice(3)}
                </h2>
            );
        }
        if (text.startsWith('### ')) {
            return out.push(
                <h3 key={i} className="mt-8 text-lg font-semibold text-ink">
                    {text.slice(4)}
                </h3>
            );
        }
        if (text.startsWith('- ')) {
            return out.push(
                <p key={i} className="flex items-start gap-3 text-[16px] leading-relaxed text-ink-body">
                    <span className="mt-[9px] h-2 w-2 shrink-0 rounded-full bg-gold-600" />
                    <span>{renderInline(text.slice(2), `b${i}`, langPrefix)}</span>
                </p>
            );
        }
        out.push(
            <p key={i} className="mt-5 text-[17px] leading-relaxed text-ink-body">
                {renderInline(text, `p${i}`, langPrefix)}
            </p>
        );
    });
    if (table.length > 0) out.push(renderTable(table, 'table-end'));
    return out;
}

export default function Article() {
    const { slug } = useParams();
    const { t, lang } = useLang();
    const [article, setArticle] = useState(null);
    const [missing, setMissing] = useState(false);
    const [related, setRelated] = useState([]);

    useEffect(() => {
        setArticle(null);
        setMissing(false);
        fetchArticle(slug)
            .then(setArticle)
            .catch(() => setMissing(true));
    }, [slug]);

    useEffect(() => {
        if (article) {
            track('kolum_article_view', { slug: article.slug, language: article.language });
            fetchArticles()
                .then((all) =>
                    setRelated(
                        all
                            .filter((a) => a.slug !== article.slug && (a.related_treatment === article.related_treatment || a.category === article.category))
                            .slice(0, 3)
                    )
                )
                .catch(() => {});
        }
    }, [article]);

    if (missing) return <NotFound />;
    if (!article) {
        return (
            <div className="mx-auto max-w-3xl px-6 py-32">
                <div className="h-8 w-2/3 animate-pulse rounded-xl bg-peri-100" />
                <div className="mt-4 h-40 animate-pulse rounded-xl bg-peri-100" />
            </div>
        );
    }

    const mins = readingMinutes(article.body);
    const treatment = article.related_treatment ? getTreatment(article.related_treatment) : null;
    const treatName = treatment ? (treatment[article.language] || treatment.en).name : null;

    return (
        <>
            <SEO
                title={article.seo_title || article.title}
                description={article.meta_description}
                path={`/kolum/${article.slug}`}
                lang={lang}
                ogType="article"
                image={article.featured_image || undefined}
                jsonLd={[
                    articleSchema({
                        lang: article.language,
                        title: article.title,
                        description: article.meta_description,
                        datePublished: article.publish_date,
                        dateModified: article.updated_date,
                        slug: article.slug,
                        author: article.author,
                        image: article.featured_image,
                    }),
                    article.faq && article.faq.length > 0 ? faqSchema(article.faq) : null,
                    breadcrumbSchema(lang, [
                        { name: t.nav.home, path: '/' },
                        { name: t.kolum.title, path: '/kolum' },
                        { name: article.title, path: `/kolum/${article.slug}` },
                    ]),
                ]}
            />
            <article className="pt-28 md:pt-36" data-testid="kolum-article">
                <Container>
                    <div className="mx-auto max-w-3xl">
                        <Reveal>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">
                                {article.category} · {article.language.toUpperCase()}
                            </p>
                            <h1 className="mt-5 font-serif text-4xl font-semibold leading-tight tracking-tight text-ink md:text-5xl">{article.title}</h1>
                            <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-peri-200 pb-6 text-sm text-ink-subtle">
                                <span className="font-semibold text-ink">{article.author}</span>
                                <span>DDS (UKM)</span>
                                <span>·</span>
                                <span>{t.kolum.published} {article.publish_date}</span>
                                {article.updated_date && article.updated_date !== article.publish_date && (
                                    <span>· {t.kolum.updated} {article.updated_date}</span>
                                )}
                                <span>· {mins} {t.kolum.minRead}</span>
                            </div>
                            {article.sample_note && (
                                <p className="mt-6 rounded-2xl border border-dashed border-peri-400 bg-peri-50 p-4 text-xs italic leading-relaxed text-ink-subtle">
                                    {article.sample_note}
                                </p>
                            )}
                        </Reveal>
                        <img src={article.featured_image || DEFAULT_KOLUM_COVER} alt={article.image_alt || article.alt_text || article.title} loading="lazy" onError={onImgError('dental')} className="mt-8 w-full rounded-3xl object-cover" data-testid="article-cover-img" />
                        <div className="mt-4" data-testid="article-body">
                            {renderBody(article.body, `/${article.language}`)}
                        </div>
                        <div className="mt-10 border-t border-peri-200 pt-8">
                            <ShareRow title={article.title} testId="dental-share" />
                        </div>

                        {article.faq && article.faq.length > 0 && (
                            <div className="mt-12">
                                <h2 className="mb-6 font-serif text-2xl font-semibold text-ink md:text-3xl">{t.treatLabels.faq}</h2>
                                <FAQ faqs={article.faq} testPrefix="article-faq" />
                            </div>
                        )}

                        {/* Author block */}
                        <div className="mt-12 rounded-3xl border border-peri-200 bg-peri-50 p-7" data-testid="article-author-block">
                            <p className="font-serif text-xl font-semibold text-ink">{article.author}</p>
                            <p className="mt-1 text-sm font-semibold text-gold-700">DDS (UKM) · Klinik Pergigian Irma Ariandi</p>
                            <p className="mt-3 text-sm leading-relaxed text-ink-muted">{t.kolum.authorLine}</p>
                            <Link to={`/dental/${lang}/doctors/dr-irma-ariandi`} className="mt-4 inline-block text-sm font-semibold text-gold-700 underline underline-offset-4" data-testid="article-author-link">
                                {t.doctors.profile} →
                            </Link>
                        </div>

                        {/* Still unsure */}
                        <div className="mt-8 rounded-3xl bg-ink p-8 text-white md:p-10" data-testid="article-still-unsure">
                            <p className="font-serif text-2xl font-semibold md:text-3xl">{t.kolum.stillUnsure.title}</p>
                            <p className="mt-4 text-[15px] leading-relaxed text-peri-200">{t.kolum.stillUnsure.text}</p>
                            <div className="mt-6">
                                <WhatsAppButton label={t.kolum.stillUnsure.cta} source="kolum-article" className="!bg-wa hover:!bg-wa-hover" testId="article-whatsapp-cta" />
                            </div>
                            <p className="mt-4 text-xs italic text-peri-500">{t.kolum.stillUnsure.note}</p>
                        </div>

                        {/* Related treatment */}
                        {treatName && (
                            <div className="mt-8 rounded-3xl border border-peri-200 bg-white p-7" data-testid="article-related-treatment">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-700">{t.kolum.relatedTreatment}</p>
                                <Link to={`/dental/${lang}/treatments/${article.related_treatment}`} onClick={() => track('kolum_treatment_click', { treatment: article.related_treatment })} className="mt-2 block font-serif text-2xl font-semibold text-ink hover:text-gold-700">
                                    {treatName} →
                                </Link>
                            </div>
                        )}
                    </div>
                </Container>
            </article>

            {related.length > 0 && (
                <section className="py-16 md:py-20" data-testid="article-related">
                    <Container>
                        <Reveal>
                            <h2 className="mb-8 font-serif text-2xl font-semibold text-ink md:text-3xl">{t.treatLabels.articles}</h2>
                        </Reveal>
                        <div className="grid gap-5 md:grid-cols-3">
                            {related.map((a, i) => (
                                <Reveal key={a.slug} delay={i * 0.07} className="h-full">
                                    <ArticleCard article={a} />
                                </Reveal>
                            ))}
                        </div>
                        <Reveal>
                            <div className="mt-8 text-center">
                                <Link to={`/dental/${lang}/kolum`} className="text-sm font-semibold text-gold-700 underline underline-offset-4">{t.kolum.back}</Link>
                            </div>
                        </Reveal>
                    </Container>
                </section>
            )}
        </>
    );
}
